import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, PageHeader } from '../../components/ui';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import api from '../../services/api';
import toast from 'react-hot-toast';

const TIP = { background:'#111', border:'1px solid rgba(46,204,113,0.2)', borderRadius:8, color:'#fff', fontSize:12 };

export default function Warmup() {
  const [accounts, setAccounts]   = useState([]);
  const [selected, setSelected]   = useState(null);
  const [history, setHistory]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [histLoading, setHistLoading] = useState(false);

  useEffect(() => { fetchAccounts(); }, []);
  useEffect(() => { if (selected) fetchHistory(selected.id); }, [selected]);

  const fetchAccounts = async () => {
    try { const r = await api.get('/warmup'); setAccounts(r.data.data || []); if (r.data.data?.length > 0) setSelected(r.data.data[0]); }
    catch {} finally { setLoading(false); }
  };

  const fetchHistory = async (id) => {
    setHistLoading(true);
    try { const r = await api.get(`/warmup/${id}/history`); setHistory(r.data.data || []); }
    catch {} finally { setHistLoading(false); }
  };

  const toggleWarmup = async (id, enabled) => {
    try { await api.put(`/warmup/${id}`, { warmupEnabled: !enabled, warmupStartedAt: !enabled ? new Date() : undefined }); fetchAccounts(); toast.success(!enabled ? '🌡️ Warmup started' : '⏸ Warmup stopped'); }
    catch { toast.error('Failed'); }
  };

  const scoreColor = (s) => s >= 70 ? '#2ECC71' : s >= 40 ? '#F39C12' : '#E74C3C';
  const scoreLabel = (s) => s >= 80 ? 'Excellent' : s >= 60 ? 'Good' : s >= 40 ? 'Fair' : 'Building';

  return (
    <div>
      <PageHeader title="Email Warmup" subtitle="Gradually build sender reputation for your accounts" />

      {loading ? <div style={{ color:'#555', textAlign:'center', padding:40 }}>Loading...</div> : (
        accounts.length === 0 ? (
          <Card style={{ textAlign:'center', padding:60 }}>
            <div style={{ fontSize:48, marginBottom:16 }}>🌡️</div>
            <h3 style={{ color:'#ccc', marginBottom:8 }}>No email accounts connected</h3>
            <p style={{ color:'#555', fontSize:14 }}>Connect an email account first, then enable warmup.</p>
          </Card>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:20 }}>
            {/* Account list */}
            <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
              {accounts.map(a => (
                <div key={a.id} onClick={() => setSelected(a)} style={{ background:'#111', border:`2px solid ${selected?.id===a.id?'#2ECC71':'rgba(46,204,113,0.12)'}`, borderRadius:16, padding:18, cursor:'pointer', transition:'all 0.2s' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                    <div>
                      <div style={{ fontWeight:700, fontSize:14, color:'#fff', marginBottom:2 }}>{a.name||a.email}</div>
                      <div style={{ fontSize:11, color:'#555' }}>{a.email}</div>
                    </div>
                    <Badge color={a.status==='warming'?'info':a.status==='active'?'success':'gray'}>{a.status}</Badge>
                  </div>
                  {/* Score ring */}
                  <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:12 }}>
                    <div style={{ position:'relative', width:56, height:56 }}>
                      <svg width="56" height="56" style={{ transform:'rotate(-90deg)' }}>
                        <circle cx="28" cy="28" r="22" fill="none" stroke="#1a1a1a" strokeWidth="5" />
                        <circle cx="28" cy="28" r="22" fill="none" stroke={scoreColor(a.warmupScore||0)} strokeWidth="5"
                          strokeDasharray={`${((a.warmupScore||0)/100)*138} 138`} strokeLinecap="round" style={{ transition:'stroke-dasharray 0.8s' }} />
                      </svg>
                      <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', fontSize:13, fontWeight:900, color:scoreColor(a.warmupScore||0) }}>{a.warmupScore||0}</div>
                    </div>
                    <div>
                      <div style={{ fontSize:15, fontWeight:800, color:scoreColor(a.warmupScore||0) }}>{scoreLabel(a.warmupScore||0)}</div>
                      <div style={{ fontSize:11, color:'#555', marginTop:2 }}>Warmup score</div>
                    </div>
                  </div>
                  {/* Stats */}
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:12, fontSize:12 }}>
                    {[['Sent Today', a.warmupSentToday||0],['Total Sent', a.warmupTotalSent||0]].map(([l,v]) => (
                      <div key={l} style={{ background:'#1a1a1a', borderRadius:8, padding:'8px 10px', textAlign:'center' }}>
                        <div style={{ fontWeight:800, color:'#fff', fontSize:14 }}>{v}</div>
                        <div style={{ color:'#555', marginTop:2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                  <Button fullWidth size="sm" variant={a.warmupEnabled?'secondary':'outline'} onClick={e=>{ e.stopPropagation(); toggleWarmup(a.id, a.warmupEnabled); }}>
                    {a.warmupEnabled ? '⏸ Stop Warmup' : '▶ Start Warmup'}
                  </Button>
                </div>
              ))}
            </div>

            {/* Score chart */}
            <div>
              {selected && (
                <>
                  <Card style={{ marginBottom:16 }}>
                    <h3 style={{ fontWeight:700, marginBottom:4 }}>📈 Warmup Score History — {selected.email}</h3>
                    <p style={{ fontSize:13, color:'#555', marginBottom:20 }}>Score progression since warmup started</p>
                    {histLoading ? <div style={{ textAlign:'center', padding:40, color:'#555' }}>Loading chart...</div> :
                      history.length === 0 ? (
                        <div style={{ textAlign:'center', padding:60, color:'#555' }}>
                          <div style={{ fontSize:36, marginBottom:12 }}>📊</div>
                          <div>No warmup history yet. Start warmup to see progress.</div>
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={260}>
                          <AreaChart data={history}>
                            <defs>
                              <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#2ECC71" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#2ECC71" stopOpacity={0.02} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="date" tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                            <YAxis domain={[0,100]} tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={TIP} formatter={(v) => [`${v}/100`, 'Score']} />
                            <Area type="monotone" dataKey="score" stroke="#2ECC71" strokeWidth={2.5} fill="url(#scoreGrad)" dot={false} />
                          </AreaChart>
                        </ResponsiveContainer>
                      )
                    }
                  </Card>

                  {/* Daily sent chart */}
                  {history.length > 0 && (
                    <Card>
                      <h3 style={{ fontWeight:700, marginBottom:20 }}>📤 Daily Warmup Emails Sent</h3>
                      <ResponsiveContainer width="100%" height={160}>
                        <AreaChart data={history}>
                          <defs>
                            <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#3498DB" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#3498DB" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <XAxis dataKey="date" tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                          <YAxis tick={{ fill:'#555', fontSize:11 }} axisLine={false} tickLine={false} />
                          <Tooltip contentStyle={TIP} />
                          <Area type="monotone" dataKey="sent" stroke="#3498DB" strokeWidth={2} fill="url(#sentGrad)" dot={false} name="Emails Sent" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </Card>
                  )}

                  {/* Warmup tips */}
                  <Card style={{ marginTop:16 }}>
                    <h3 style={{ fontWeight:700, marginBottom:14 }}>💡 Warmup Tips</h3>
                    {[
                      ['Week 1–2', 'Score 0–30', 'Sending 3–8 emails/day. Focus on getting replies to improve reputation.', '#555'],
                      ['Week 3–4', 'Score 30–60', 'Ramping up to 15–25 emails/day. Your domain is building trust.', '#F39C12'],
                      ['Week 5+',  'Score 60–100','Full warmup: 35–40 emails/day. Ready for campaigns.', '#2ECC71'],
                    ].map(([period, score, tip, color]) => (
                      <div key={period} style={{ display:'flex', gap:12, padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                        <div style={{ width:70, flexShrink:0 }}>
                          <div style={{ fontSize:11, fontWeight:800, color, textTransform:'uppercase' }}>{period}</div>
                          <div style={{ fontSize:10, color:'#444', marginTop:2 }}>{score}</div>
                        </div>
                        <div style={{ fontSize:12, color:'#666', lineHeight:1.6 }}>{tip}</div>
                      </div>
                    ))}
                  </Card>
                </>
              )}
            </div>
          </div>
        )
      )}
    </div>
  );
}
